import {AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, Optional, ViewChild} from "@angular/core";
import {SelectionModel} from "@angular/cdk/collections";
import {
	MatTableDataSource,
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from "@angular/material/table";
import {MatDialog, MatDialogRef, MatDialogContent} from "@angular/material/dialog";
import {UserNotificationService} from "../../core/services/services/userNotification.service";
import {AuthenticationService} from "../../core/services/services/authentication.service";
import {AssignmentProjectEmployeeService} from "../../core/services/services/assignment-project-employee.service";
import {AssignmentStatus} from "../../core/models/Enums/AssignmentStatus";
import {ApplicationUser} from "../../core/models/ApplicationUser";
import {catchError, firstValueFrom, forkJoin, map, of} from "rxjs";
import {TranslateService, TranslatePipe} from "@ngx-translate/core";
import {Notification} from "../../core/models/Notification";
import {MatSort, MatSortHeader} from "@angular/material/sort";
import moment from "moment/moment";
import {TranslatableComponent} from "../../core/models/TranslatableComponent";
import {PaginatorSmartComponent} from "../paginator-smart/components/paginator-smart.component";
import {UserNotification} from "../../core/models/UserNotification";
import {Router, RouterLink} from "@angular/router";
import {NgClass, NgTemplateOutlet} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {MatButtonToggleGroup, MatButtonToggle} from "@angular/material/button-toggle";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatButton} from "@angular/material/button";
import {DialogData, InfoDialogComponent} from "../info-dialog/components/info-dialog.component";

@Component({
	selector: "app-assignments-notification-dialog",
	templateUrl: "./assignments-notification-dialog.component.html",
	styleUrls: ["./assignments-notification-dialog.component.scss"],
	imports: [
		NgClass,
		NgTemplateOutlet,
		MatIcon,
		MatDialogContent,
		MatButtonToggleGroup,
		MatButtonToggle,
		MatFormField,
		MatLabel,
		MatInput,
		MatProgressBar,
		MatTable,
		MatSort,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCheckbox,
		MatCellDef,
		MatCell,
		MatSortHeader,
		RouterLink,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		PaginatorSmartComponent,
		MatButton,
		TranslatePipe,
	],
})
export class AssignmentsNotificationDialogComponent extends TranslatableComponent implements OnInit, AfterViewInit {
	@Input() isDialog: boolean = true; // Bestimmt ob als Dialog oder Karte angezeigt
	@Input() showHeader: boolean = true; // Zeigt/versteckt den Header
	@Input() showPagination: boolean = true; // Zeigt/versteckt die Pagination
	@Input() showActions: boolean = true; // Zeigt/versteckt die Action-Buttons

	selection = new SelectionModel<UserNotification>(true, []);
	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(PaginatorSmartComponent) paginator: PaginatorSmartComponent;

	displayedColumns: string[];
	loading: boolean;
	dataSource: MatTableDataSource<UserNotification> = new MatTableDataSource();
	notifications: UserNotification[] = [];
	archiveOpen: boolean = false;
	gotUser: boolean;
	private applicationUser: ApplicationUser;
	private viewInitialized = false;

	constructor(
		@Optional() public dialogRef: MatDialogRef<AssignmentsNotificationDialogComponent>,
		private userNotificationService: UserNotificationService,
		private authenticationService: AuthenticationService,
		private assignmentProjectEmployeeService: AssignmentProjectEmployeeService,
		private translateService: TranslateService,
		private router: Router,
		private cd: ChangeDetectorRef,
		private matDialog: MatDialog,
	) {
		super(translateService, "Notifications");
	}

	async ngOnInit() {
		this.loading = true;
		this.dataSource.filterPredicate = (data: any, filter: string) => {
			const dataStr = Object.values(data).join(" ").toLowerCase();
			return dataStr.includes(filter.trim().toLowerCase());
		};

		this.applicationUser = await this.authenticationService.getApplicationUser();
		this.gotUser = !!this.applicationUser?.employee?.id;
		if (this.gotUser) {
			await this.openNotifications(false);
		} else {
			this.loading = false;
		}

		this.cd.detectChanges();
	}

	ngAfterViewInit() {
		this.viewInitialized = true;
		this.attachTableFeatures();
		this.cd.detectChanges();
	}

	applyFilter(event: Event) {
		this.dataSource.filter = (event.target as HTMLInputElement).value;
	}

	openProjectSite(id) {
		const url = this.router.serializeUrl(this.router.createUrlTree(["/project-edit/" + id]));
		window.open(url, "_blank");
	}

	hasInformation() {
		let assignmentsToDecide = this.selection.selected;
		return assignmentsToDecide.length === 0;
	}

	async getNotificationData(showArchive: boolean) {
		let notifies: UserNotification[];
		if (showArchive) {
			this.displayedColumns = ["sender", "recipient", "createdAt", "assignmentStatus", "employeeText", "type"];
		} else {
			this.displayedColumns = [
				"checkBox",
				"sender",
				"recipient",
				"createdAt",
				"assignmentStatus",
				"employeeText",
				"type",
			];
		}

		if (showArchive) {
			notifies = await firstValueFrom(
				this.userNotificationService.getNotificationArchiveByUserId(this.applicationUser.employee.id),
			);
		} else {
			notifies = await firstValueFrom(
				this.userNotificationService.getNotificationByUserId(this.applicationUser.employee.id),
			);
		}

		notifies.forEach((notification) => {
			if (notification != null) {
				let space = " ";
				notification.type = this._lang(notification.type);
				notification.assignmentStatus = this._lang(notification.assignmentStatus);
				notification.employeeText =
					this._lang("assignment") + ":" + space + notification.assignee + space + this._lang("toProject") + space;
				notification.capacityText =
					space +
					this._lang("from") +
					space +
					this.formatDate(notification.assignmentEffectiveFrom) +
					space +
					this._lang("to") +
					space +
					this.formatDate(notification.assignmentEffectiveUntil) +
					space +
					this._lang("with") +
					space +
					notification.assignmentCapacity +
					this._lang("capacity");
				this.notifications.unshift(notification);
			}
		});

		this.dataSource.data = this.notifications;
		this.attachTableFeatures();
		this.userNotificationService.notificationRead(this.applicationUser.employee.id).subscribe();
		this.loading = false;
		this.cd.detectChanges();
	}

	private attachTableFeatures() {
		if (!this.viewInitialized) {
			return;
		}

		if (this.sort) {
			this.dataSource.sort = this.sort;
		}

		if (this.showPagination && this.paginator?.basePaginator) {
			this.dataSource.paginator = this.paginator.basePaginator;
		}
	}

	async openNotifications(isArchiveOpened: boolean) {
		this.selection.clear();
		this.notifications = [];
		this.archiveOpen = isArchiveOpened;
		await this.getNotificationData(isArchiveOpened);
	}

	async showActiveAssignments() {
		this.loading = true;
		this.dataSource.data = [];
		await this.openNotifications(false);
	}

	async showArchive() {
		this.loading = true;
		this.dataSource.data = [];
		await this.openNotifications(true);
	}

	formatDate(date: Date | string): string {
		if (date) {
			return moment(date).format("DD.MM.YYYY");
		} else {
			return "";
		}
	}

	async getStatus(assigment): Promise<string> {
		switch (assigment.status) {
			case AssignmentStatus.APPROVED:
				return this.translateService.instant("mep.enums.status.approved");
			case AssignmentStatus.REJECTED:
				return this.translateService.instant("mep.enums.status.rejected");
			case AssignmentStatus.PENDING:
				return this.translateService.instant("mep.enums.status.pending");
			case AssignmentStatus.INFORMATION:
				return this.translateService.instant("mep.enums.status.information");
			default:
				return "-";
		}
	}

	masterToggle() {
		if (this.isAllSelected()) {
			this.selection.clear();
		} else {
			this.dataSource.filteredData.forEach((row: UserNotification) => this.selection.select(row));
		}
	}

	isAllSelected() {
		const numSelected = this.selection.selected.length;
		const numRows = this.dataSource.filteredData.length;
		return numSelected === numRows;
	}

	acceptAssignment() {
		this.decideAssignment(true);
	}

	declineAssignment() {
		this.decideAssignment(false);
	}

	decideAssignment(decide: boolean) {
		// true = accept / false = decline
		const toDecide = this.selection.selected.filter((n) => n.type !== this._lang("Notification"));
		forkJoin(
			toDecide.map((n) =>
				this.userNotificationService.decideNotification(decide, n.notificationId, n.assignmentId).pipe(
					map((alreadyDecided) => (alreadyDecided ? n : null)),
					// "Notification not found" zeigt der Error-Interceptor an
					catchError(() => of(null)),
				),
			),
		).subscribe((results) => {
			const alreadyDecided = results.filter((n) => n !== null);
			this.openNotifications(this.archiveOpen);
			if (alreadyDecided.length > 0) {
				const data: DialogData[] = alreadyDecided.map((n) => ({first: n.type, second: n.employeeText + n.projectName}));
				const dialogRef = this.matDialog.open(InfoDialogComponent, {data});
				dialogRef.componentInstance.dialogTitle = this._lang("alreadyProcessedTitle");
				dialogRef.componentInstance.dialogDescription = this._lang("alreadyProcessedDescription");
				dialogRef.componentInstance.showCopyButton = false;
			}
		});
	}

	closeDialog() {
		if (this.dialogRef) {
			this.dialogRef.close();
		}
	}

	getUnreadNotificationCount(): number {
		return this.notifications.filter((n) => !n.read).length;
	}
}
